// AI citation pill with hover preview tooltip and real navigation (AC-TRUST-3 / AC-AI-3).
//
// Every citation must navigate to a useful destination or explain why it can't:
// - When the citation's target id resolves to a record route (see CITATION_ROUTE_TABLE),
//   the chip renders as a react-router <Link> to that record view.
// - When no route resolves but the caller supplies `onOpen`, the chip stays a button
//   so the surface can open its own drawer/inspector.
// - Otherwise the chip renders inert with title "No linked record view" — never a
//   dead click and never a toast-only citation.
// Hover still lazily resolves knowledge artifacts to /api/artifacts/:id for a preview.

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/apiClient.js';

// Prefix → route table for canonical seeded record ids. Order matters: the first
// matching prefix wins (e.g. keep 'KB-' after any longer 'KB…-' style prefixes).
const CITATION_ROUTE_TABLE = [
  { prefix: 'ART-', route: (id) => `/knowledge/artifacts/${id}` },
  { prefix: 'KNW-', route: (id) => `/knowledge/artifacts/${id}` },
  { prefix: 'KB-', route: (id) => `/knowledge/artifacts/${id}` },
  { prefix: 'AIRUN-', route: (id) => `/ai/runs/${id}` },
  { prefix: 'BRF-', route: (id) => `/briefings/${id}` },
  { prefix: 'PF-', route: (id) => `/portfolios/${id}` },
  { prefix: 'PGM-', route: (id) => `/programs/${id}` },
  { prefix: 'PRJ-', route: (id) => `/projects/${id}` },
  { prefix: 'INT-', route: (id) => `/projects/${id}` },
  { prefix: 'FIN-', route: () => '/financials' },
  { prefix: 'FUND-', route: () => '/financials/funding-sources' },
  { prefix: 'RSK-', route: () => '/governance/risks' },
  { prefix: 'ISS-', route: () => '/governance/issues' },
  { prefix: 'CHG-', route: () => '/governance/changes' },
  { prefix: 'LL-', route: () => '/knowledge/lessons-learned' },
  { prefix: 'ACT-', route: (id) => `/actions/${id}` },
  { prefix: 'WI-', route: (id) => `/work-items/${id}` },
  { prefix: 'MTG-', route: (id) => `/meetings/${id}` },
  { prefix: 'ALLOC-', route: (id) => `/resources/allocations/${id}` },
  { prefix: 'LOE-', route: (id) => `/analytics/loes/${id}` },
];

/**
 * Resolve a canonical record id (ART-…, AIRUN-…, PRJ-…) to its in-app route.
 * Returns null when the id has no linked record view.
 */
export function citationRoute(id) {
  if (typeof id !== 'string' || !id.trim()) return null;
  const trimmed = id.trim();
  const entry = CITATION_ROUTE_TABLE.find(({ prefix }) => trimmed.startsWith(prefix));
  return entry ? entry.route(trimmed) : null;
}

/**
 * Resolve a citation object (any of the shapes PRIME emits: `{source}`,
 * `{artifactId}`, `{canonicalEntityId}`, …) to `{ id, to }` for the first
 * candidate id that has a record route, or null when none navigates.
 */
export function resolveCitationTarget(citation) {
  if (!citation) return null;
  if (typeof citation === 'string') {
    const to = citationRoute(citation);
    return to ? { id: citation, to } : null;
  }
  const candidates = [
    citation.artifactId,
    citation.source,
    citation.canonicalEntityId,
    citation.citationId,
    citation.id,
  ];
  for (const candidate of candidates) {
    const to = citationRoute(candidate);
    if (to) return { id: candidate, to };
  }
  return null;
}

/**
 * Inline id → record-view link. Renders a <Link> when the id resolves,
 * otherwise an inert span titled "No linked record view".
 */
export function CitationSourceLink({ id, className, children }) {
  const to = citationRoute(id);
  const content = children ?? id;
  if (!to) {
    return (
      <span className={className} title="No linked record view">
        {content}
      </span>
    );
  }
  return (
    <Link className={className} to={to}>
      {content}
    </Link>
  );
}

export function AiCitationChip({ citation, index, onOpen }) {
  const [hover, setHover] = useState(false);
  const [resolved, setResolved] = useState(null);
  const fetchedRef = useRef(false);
  useEffect(() => {
    if (!hover || fetchedRef.current) return;
    const artifactId = citation?.artifactId || citation?.source || citation?.citationId;
    if (!artifactId || !/^ART-|^KNW-|^KB-/.test(artifactId)) {
      fetchedRef.current = true;
      return;
    }
    fetchedRef.current = true;
    api
      .get(`/artifacts/${artifactId}`)
      .then((r) => setResolved(r?.data || null))
      .catch(() => setResolved(null));
  }, [hover, citation]);

  const target = resolveCitationTarget(citation);
  const describedBy = `citation-${index}`;
  const body = (
    <>
      <span className="citation-index">[{index + 1}]</span>
      <span className="citation-source">{citation.source || target?.id || citation.title}</span>
      {citation.score != null ? (
        <span className="citation-score">{Number(citation.score).toFixed(2)}</span>
      ) : null}
    </>
  );

  return (
    <span
      className="citation-chip-wrap"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
    >
      {target ? (
        // Navigation is the governed behavior (AC-TRUST-3); it supersedes onOpen.
        <Link className="citation-chip" to={target.to} aria-describedby={describedBy}>
          {body}
        </Link>
      ) : onOpen ? (
        <button
          type="button"
          className="citation-chip"
          onClick={() => onOpen(citation, resolved)}
          aria-describedby={describedBy}
        >
          {body}
        </button>
      ) : (
        <span
          className="citation-chip citation-chip--static"
          title="No linked record view"
          aria-describedby={describedBy}
        >
          {body}
        </span>
      )}
      {hover ? (
        <div className="citation-tooltip" role="tooltip" id={describedBy}>
          <div className="citation-tooltip-head">{citation.source}</div>
          <p>{citation.excerpt}</p>
          {resolved ? (
            <div className="citation-tooltip-meta">
              {resolved.title || resolved.name} ·{' '}
              {resolved.documentType || resolved.type || 'evidence'}
            </div>
          ) : null}
        </div>
      ) : null}
    </span>
  );
}

export function AiCitationStrip({ citations = [], onOpen }) {
  if (!citations.length) return null;
  return (
    <div className="citation-strip">
      {citations.map((c, i) => (
        <AiCitationChip key={c.citationId || c.id || i} citation={c} index={i} onOpen={onOpen} />
      ))}
    </div>
  );
}
